// Ported from dev-scrips-pm-react src/billing/pages/invoice-detail-page.tsx (PROD-3312 appointment Billing tab):
// the Draft-invoice edit handlers. pm-react writes them inline in the page; here they are one hook so the exact
// request bodies are testable apart from the page. Every handler is one `PUT /api/Billing/Invoice/{invoiceId}`
// through `useInvoiceAddRemoveService`, every body carries `updateType: ''` ('PreClaim' is the claim workbench's).
//
// Two id spaces, as in pm-react (PROD-1006): ADD takes the service-MASTER id (from the Services/Fees catalogue);
// REMOVE, QTY and every per-line edit take the invoice-LINE id (`invoice.services[].id`).
import { useInvoiceAddRemoveService, type AddRemoveServiceRequest } from '@/billing/services/use-add-remove-service';

export interface InvoiceLineEdits {
  /** Add a catalogue service (master id). */
  addService: (masterServiceId: string) => void;
  removeService: (lineId: string) => void;
  changeQty: (lineId: string, quantity: number) => void;
  /** Coverage flip: exactly one of the three id lists carries the line, the other two are `[]`. */
  changeCoverage: (lineId: string, coverage: string) => void;
  /** A complete no-Z ISO `YYYY-MM-DDThh:mm:ss` from the service-date picker, or ''. */
  changeServiceDate: (lineId: string, serviceAddedDate: string) => void;
  changeAuthCode: (lineId: string, authCode: string) => void;
  /** `YYYY-MM-DD` start / end (or undefined); shipped as no-Z `YYYY-MM-DDT00:00:00`, or null when cleared. */
  changeValidity: (lineId: string, startYmd: string | undefined, endYmd: string | undefined) => void;
  /** A recalc is in flight — the table disables its edit controls. */
  pending: boolean;
}

export function useInvoiceLineEdits(invoiceId: string | undefined): InvoiceLineEdits {
  const mutation = useInvoiceAddRemoveService();
  // `invoiceId` is guarded in the page (it renders the error state without one); the guard here keeps the hook
  // callable above the page's early returns.
  const send = (request: AddRemoveServiceRequest) => {
    if (!invoiceId) return;
    mutation.mutate({ invoiceId, request });
  };
  return {
    addService: (masterServiceId) => send({ addedServices: [masterServiceId], updateType: '' }),
    removeService: (lineId) => send({ removeServices: [lineId], updateType: '' }),
    changeQty: (lineId, quantity) => send({ updateServiceQuantity: [{ id: lineId, quantity }], updateType: '' }),
    changeCoverage: (lineId, coverage) =>
      send({
        includedServices: coverage === 'Included' ? [lineId] : [],
        excludedServices: coverage === 'Excluded' ? [lineId] : [],
        needsApprovalServices: coverage === 'Needs Approval' ? [lineId] : [],
        updateType: '',
      }),
    changeServiceDate: (lineId, serviceAddedDate) =>
      send({ updateServiceDate: [{ id: lineId, serviceAddedDate }], updateType: '' }),
    changeAuthCode: (lineId, authCode) => send({ updateAuthCode: [{ id: lineId, authCode }], updateType: '' }),
    changeValidity: (lineId, startYmd, endYmd) =>
      send({
        updateValidity: [
          {
            id: lineId,
            validityStartDate: startYmd ? `${startYmd}T00:00:00` : null,
            validityEndDate: endYmd ? `${endYmd}T00:00:00` : null,
          },
        ],
        updateType: '',
      }),
    pending: mutation.isPending,
  };
}
